import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import api from '../../services/Api';

const ActivityBarChart = ({
  data = null,
  sentColor = '#1814F3',
  receivedColor = '#16DBCC',
  title = 'Quotes Sent/Received Overview',
  businessId = '',
}) => {
  const [typeFilter, setTypeFilter] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState('All');
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sentCount, setSentCount] = useState(0);
  const [receivedCount, setReceivedCount] = useState(0);
  const [rawQuotes, setRawQuotes] = useState({ sent: [], received: [] });

  useEffect(() => {
    const fetchQuoteData = async () => {
      if (!businessId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await api.get(`/admin/business/${businessId}`);

        if (response.data.message === 'business retrieved successfully') {
          const quotes = response.data.business.quotes || [];
          const sentQuotes = quotes.filter(quote => quote.by === businessId);
          const receivedQuotes = quotes.filter(quote => quote.by !== businessId);

          setRawQuotes({ sent: sentQuotes, received: receivedQuotes });
          processQuotes(sentQuotes, receivedQuotes);
        }
      } catch (error) {
        console.error('Error fetching business data:', error);
        setChartData([]);
        setSentCount(0);
        setReceivedCount(0);
      } finally {
        setLoading(false);
      }
    };

    fetchQuoteData();
  }, [businessId]);

  useEffect(() => {
    if (rawQuotes.sent.length > 0 || rawQuotes.received.length > 0) {
      const { filteredSent, filteredReceived } = filterQuotesByDate(
        rawQuotes.sent,
        rawQuotes.received,
        selectedMonth,
        selectedYear
      );
      processQuotes(filteredSent, filteredReceived);
    }
  }, [selectedMonth, selectedYear, rawQuotes]);

  const filterQuotesByDate = (sentQuotes, receivedQuotes, month, year) => {
    if (year === 'All') {
      return { filteredSent: sentQuotes, filteredReceived: receivedQuotes };
    }

    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0, 23, 59, 59, 999);

    const filteredSent = sentQuotes.filter(quote => {
      try {
        const quoteDate = new Date(quote.createdAt);
        return quoteDate >= startDate && quoteDate <= endDate;
      } catch (e) {
        console.error('Error filtering sent quote:', quote, e);
        return false;
      }
    });

    const filteredReceived = receivedQuotes.filter(quote => {
      try {
        const quoteDate = new Date(quote.createdAt);
        return quoteDate >= startDate && quoteDate <= endDate;
      } catch (e) {
        console.error('Error filtering received quote:', quote, e);
        return false;
      }
    });

    return { filteredSent, filteredReceived };
  };

  const processQuotes = (sentQuotes, receivedQuotes) => {
    if (sentQuotes.length === 0 && receivedQuotes.length === 0) {
      setChartData([]);
      setSentCount(0);
      setReceivedCount(0);
      return;
    }

    const quotesByDay = {};

    const process = (quotes, type) => {
      quotes.forEach(quote => {
        try {
          const date = new Date(quote.createdAt);
          const dayKey = date.toISOString().split('T')[0];

          if (!quotesByDay[dayKey]) {
            quotesByDay[dayKey] = {
              date,
              sent: 0,
              received: 0,
            };
          }
          quotesByDay[dayKey][type]++;
        } catch (e) {
          console.error('Error processing quote:', quote, e);
        }
      });
    };

    process(sentQuotes, 'sent');
    process(receivedQuotes, 'received');

    const daysArray = Object.values(quotesByDay).sort((a, b) => a.date - b.date);

    const formatData = daysArray.map(day => {
      const monthName = day.date.toLocaleString('default', { month: 'long' });
      const dayNumber = String(day.date.getDate()).padStart(2, '0');
      return {
        label: `${monthName} ${dayNumber}`,
        sent: day.sent,
        received: day.received,
        date: day.date,
      };
    });

    setChartData(formatData);
    setSentCount(sentQuotes.length);
    setReceivedCount(receivedQuotes.length);
  };

  const typeFilterOptions = ['All', 'Sent Quotes', 'Received Quotes'];

  const getFilteredData = () => {
    const displayData = data && data.length > 0 ? data : chartData;

    if (typeFilter === 'All') {
      return displayData;
    } else if (typeFilter === 'Sent Quotes') {
      return displayData.map(item => ({
        label: item.label,
        sent: item.sent,
      }));
    } else {
      return displayData.map(item => ({
        label: item.label,
        received: item.received,
      }));
    }
  };

  const currentYear = new Date().getFullYear();
  const yearOptions = ['All', ...Array.from({ length: 5 }, (_, i) => currentYear - i)];

  return (
    <div className="w-full p-4">
      <div className="flex items-center justify-between">
        <h2 className="mb-6 text-base font-semibold text-black">{title}</h2>
        <div className="flex justify-end gap-2 mb-4">
          <div className="flex space-x-2">
            {/* Month Dropdown */}
            <select
              className="border rounded p-1 text-[10px] text-[#4A5154] bg-[#F5F6F7]"
              value={selectedMonth}
              onChange={e => setSelectedMonth(Number(e.target.value))}
              disabled={selectedYear === 'All'}>
              {Array.from({ length: 12 }, (_, i) => (
                <option key={i + 1} value={i + 1}>
                  {new Intl.DateTimeFormat('en-US', { month: 'long' }).format(new Date(2025, i))}
                </option>
              ))}
            </select>

            {/* Year Dropdown */}
            <select
              className="border rounded p-1 text-[10px] text-[#4A5154] bg-[#F5F6F7]"
              value={selectedYear}
              onChange={e =>
                setSelectedYear(e.target.value === 'All' ? 'All' : Number(e.target.value))
              }>
              {yearOptions.map(year => (
                <option key={year} value={year}>
                  {year === 'All' ? 'All Time' : year}
                </option>
              ))}
            </select>
          </div>
          <div className="relative">
            <select
              className="block px-4 py-2 pr-8 text-xs text-gray-600 bg-white border border-gray-200 rounded-md appearance-none hover:border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={typeFilter}
              onChange={e => setTypeFilter(e.target.value)}>
              {typeFilterOptions.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center px-2 text-gray-700 pointer-events-none">
              <svg
                className="w-4 h-4 fill-current"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 20 20">
                <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div className="flex justify-between gap-4 mb-2">
        <div>
          <h2 className="mb-2 text-xs text-gray-500">
            {sentCount} Quotes Sent & {receivedCount} Quotes Received
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: sentColor }}></div>
            <span className="text-xs text-gray-500">Sent</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: receivedColor }}></div>
            <span className="text-xs text-gray-500">Received</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-8 h-8 border-b-2 border-blue-500 rounded-full animate-spin"></div>
        </div>
      ) : getFilteredData().length === 0 ? (
        <div className="flex items-center justify-center h-40 text-gray-500">
          No quote data available for the selected period
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={getFilteredData()} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f0f0f0"
              vertical={false}
              horizontal={true}
            />
            <XAxis
              dataKey="label"
              axisLine={false}
              tickLine={false}
              tick={{ fill: '#8190A5', fontSize: 10 }}
            />
            <YAxis axisLine={false} tickLine={false} tick={{ fill: '#8190A5', fontSize: 10 }} />
            <Tooltip />
            {(typeFilter === 'All' || typeFilter === 'Sent Quotes') && (
              <Bar
                dataKey="sent"
                name="Sent Quotes"
                fill={sentColor}
                radius={[10, 10, 10, 10]}
                barSize={20}
              />
            )}
            {(typeFilter === 'All' || typeFilter === 'Received Quotes') && (
              <Bar
                dataKey="received"
                name="Received Quotes"
                fill={receivedColor}
                radius={[10, 10, 10, 10]}
                barSize={20}
              />
            )}
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

ActivityBarChart.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  sentColor: PropTypes.string,
  receivedColor: PropTypes.string,
  title: PropTypes.string,
  businessId: PropTypes.string,
};

export default ActivityBarChart;
